import { toast } from 'sonner';
import type { QueryClient } from '@tanstack/react-query';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { refreshMembershipsWhenSubscriptionUnreachable } from '@/components/organisms/retention/membership-cancel-errors';
import type { ApiError } from '@/lib/api/errors';
import { membershipKeys } from '@/lib/queryKeys';
import { membershipEndpoints } from '@/lib/api/endpoints';
import { apiPost, apiPut, operationsClient } from '@/lib/api';
import type {
  Membership,
  CreateMembershipDto,
  UpdateMembershipDto,
  ChangeMembershipPlanDto,
  CancelMembershipDto,
  SaveMembershipOfferDto,
} from '@/types/membership';

type MembershipResponse = { data: Membership };

function refreshWhenSubscriptionUnreachable(
  error: ApiError,
  _variables: unknown,
  queryClient: QueryClient,
) {
  refreshMembershipsWhenSubscriptionUnreachable(error, queryClient);
}

export const useCreateMembership = createMutationHook<Membership, CreateMembershipDto>({
  mutationFn: async (dto) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.create(),
      dto,
    );
    return response.data;
  },
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    toast.success('Membership created');
  },
});

export const useUpdateMembership = createMutationHook<Membership, UpdateMembershipDto>({
  mutationFn: async ({ id, ...dto }) => {
    const response = await apiPut<MembershipResponse>(
      operationsClient,
      membershipEndpoints.update(id),
      dto,
    );
    return response.data;
  },
  onSuccess: (membership, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(membership.id) });
    toast.success('Membership updated');
  },
  onError: refreshWhenSubscriptionUnreachable,
});

export const useChangeMembershipPlan = createMutationHook<Membership, ChangeMembershipPlanDto>({
  mutationFn: async ({ id, ...dto }) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.changePlan(id),
      dto,
    );
    return response.data;
  },
  onSuccess: (membership, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(membership.id) });
  },
  onError: refreshWhenSubscriptionUnreachable,
});

export const useActivateMembership = createMutationHook<Membership, string>({
  mutationFn: async (id) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.activate(id),
    );
    return response.data;
  },
  onSuccess: (membership, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    window.setTimeout(() => {
      void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(membership.id) });
      void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    }, 1_500);
    toast.success('Membership activated');
  },
  onError: refreshWhenSubscriptionUnreachable,
});

export const useRepairMembershipIncludedVisits = createMutationHook<Membership, string>({
  mutationFn: async (id) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.repairIncludedVisits(id),
    );
    return response.data;
  },
  onSuccess: (membership, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(membership.id) });
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    toast.success(
      membership.status === 'ACTIVE'
        ? 'Included visits repaired and membership activated'
        : 'Included visits repaired; activation is still pending',
    );
  },
  onError: refreshWhenSubscriptionUnreachable,
});

export const useCreatePrepaidMembershipInvoice = createMutationHook<Membership, string>({
  mutationFn: async (id) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.prepaidInvoice(id),
    );
    return response.data;
  },
  onSuccess: (membership, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(membership.id) });
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    toast.success('Prepaid invoice created');
  },
});

export const useCancelMembership = createMutationHook<
  Membership,
  { id: string; dto: CancelMembershipDto }
>({
  mutationFn: async ({ id, dto }) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.cancel(id),
      dto,
    );
    return response.data;
  },
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    toast.success('Membership cancelled');
  },
  onError: refreshWhenSubscriptionUnreachable,
});

export const useRecordMembershipSaveOffer = createMutationHook<
  Membership,
  { id: string; dto: SaveMembershipOfferDto }
>({
  mutationFn: async ({ id, dto }) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.saveOffer(id),
      dto,
    );
    return response.data;
  },
  onSuccess: (membership, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(membership.id) });
    toast.success('Save offer recorded');
  },
  onError: refreshWhenSubscriptionUnreachable,
});

export const useSuspendMembership = createMutationHook<Membership, string>({
  mutationFn: async (id) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.suspend(id),
    );
    return response.data;
  },
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    toast.success('Membership suspended');
  },
  onError: refreshWhenSubscriptionUnreachable,
});

export const useReactivateMembership = createMutationHook<Membership, string>({
  mutationFn: async (id) => {
    const response = await apiPost<MembershipResponse>(
      operationsClient,
      membershipEndpoints.reactivate(id),
    );
    return response.data;
  },
  onSuccess: (_data, _variables, queryClient) => {
    void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
    toast.success('Membership reactivated');
  },
  onError: refreshWhenSubscriptionUnreachable,
});
